import { LargeSearchIcon, SettingsIcon } from '@orn/shared-web/icons';
import { Link as RouterLink } from 'react-router-dom';

import { PATHS } from '../../paths';
import MantleHorizontalLockupPrimary from '../logos/MantleHorizontalLockupPrimary';

import MantleAssistantNavButton from './MantleAssistantNavButton';
import NavAccountCard from './NavAccountCard';
import NavItem from './NavItem';
import NavList from './NavList';
import NotificationBellButton from './NotificationBellButton';

import type { NavItemType } from './config';
import type { LoggedInUser } from '../../providers/AuthProvider';

interface IFullSidebarContentProps {
  user: LoggedInUser;
  activeUrl?: string;
  items: NavItemType[];
  footerItems?: NavItemType[];
  showAccountCard?: boolean;
  /**
   * When `true`, render the "Ask Mantle" button at the top of the footer.
   * Caller (typically `MainSidebar`) is responsible for resolving the
   * `feat-assistant` gate.
   */
  showAssistantButton?: boolean;
  /**
   * When `true`, render the alerts bell in the header actions group. Caller
   * is responsible for resolving the `feat-alerts-center` gate.
   */
  showAlertsBell?: boolean;
  /** Unread alert count shown on the bell's pip. */
  alertsUnreadCount?: number;
  /** Whether the alerts route is the active one. */
  isAlertsCurrent?: boolean;
  /** Opens the center in the `xl+` side panel instead of navigating. */
  onOpenAlertsInPanel?: () => void;
  onOpenSearch: () => void;
}

/**
 * Inner content shared by the desktop fixed sidebar (`BaseSidebar`) and the
 * mobile nav drawer (`MobileNavDrawer`): header (Mantle wordmark + alerts
 * bell), nav list, and a footer holding the assistant button, the Search and
 * Settings rows, and the account card. Pure presentational — receives
 * `onOpenSearch` from a parent that owns the search modal state.
 */
const FullSidebarContent = ({
  user,
  activeUrl,
  items,
  footerItems = [],
  showAccountCard = true,
  showAssistantButton = false,
  showAlertsBell = false,
  alertsUnreadCount = 0,
  isAlertsCurrent = false,
  onOpenAlertsInPanel,
  onOpenSearch,
}: IFullSidebarContentProps) => {
  return (
    <aside className="flex h-full w-full flex-col justify-between overflow-auto pt-4 lg:pt-6">
      <div className="flex w-full items-center justify-between px-4 lg:px-5">
        <RouterLink
          to={PATHS.DASHBOARD}
          aria-label="Mantle home"
          className="outline-focus-ring flex h-8 items-center rounded-md transition-opacity hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-2"
        >
          <MantleHorizontalLockupPrimary className="h-8 w-auto max-w-[88px]" aria-hidden="true" />
        </RouterLink>
        <div className="flex items-center gap-0.5">
          {showAlertsBell && (
            <NotificationBellButton
              unreadCount={alertsUnreadCount}
              current={isAlertsCurrent}
              onOpenInPanel={onOpenAlertsInPanel}
            />
          )}
        </div>
      </div>

      <NavList activeUrl={activeUrl} items={items} />

      <div className="mt-auto flex flex-col gap-4 px-2 py-4 lg:px-4 lg:py-6">
        {showAssistantButton && <MantleAssistantNavButton />}

        <ul className="flex flex-col">
          {footerItems.map((item) => (
            <li key={item.label} className="py-0.5">
              <NavItem
                icon={item.icon}
                href={item.href}
                type="link"
                current={(item.href?.split('?')[0] ?? '') === activeUrl}
              >
                {item.label}
              </NavItem>
            </li>
          ))}
          <li className="py-0.5">
            <NavItem icon={LargeSearchIcon} type="button" onClick={onOpenSearch}>
              Search
            </NavItem>
          </li>
          <li className="py-0.5">
            <NavItem icon={SettingsIcon} href={PATHS.SETTINGS} type="link" current={activeUrl === PATHS.SETTINGS}>
              Settings
            </NavItem>
          </li>
        </ul>

        {showAccountCard && <NavAccountCard user={user} />}
      </div>
    </aside>
  );
};

export default FullSidebarContent;
